import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { doc, getDoc } from 'firebase/firestore';

const RecordFilter = ({ records, onFilterChange }) => {
  const [filters, setFilters] = useState({
    search: '',
    dateRange: 'all',
    tags: [],
    company: ''
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadUserTags();
    extractCompanies();
  }, [records]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyFilters();
  }, [filters, records]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserTags = async () => {
    if (!auth.currentUser) return;
    
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().tags) {
        setAvailableTags(userDoc.data().tags);
      }
    } catch (error) {
      console.error('載入標籤失敗:', error);
    }
  };

  const extractCompanies = () => {
    const companies = [...new Set(
      records.map(record => 
        record['公司名稱'] || record['Company Name'] || record['Company']
      ).filter(Boolean)
    )];
    setAvailableCompanies(companies);
  };

  const applyFilters = () => {
    let filtered = [...records];

    // 文字搜尋
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(record => 
        Object.values(record).some(value => 
          value && value.toString().toLowerCase().includes(searchLower)
        )
      );
    }

    // 日期範圍
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          filterDate.setDate(now.getDate());
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          // 'all' case - no filtering needed
          break;
      }
      
      if (filters.dateRange !== 'all') {
        filtered = filtered.filter(record => {
          const recordDate = record.timestamp?.toDate?.() || new Date(record.timestamp || 0);
          return recordDate >= filterDate;
        });
      }
    }

    // 標籤篩選
    if (filters.tags.length > 0) {
      filtered = filtered.filter(record => 
        filters.tags.every(tag => record.tags?.includes(tag))
      );
    }

    // 公司篩選
    if (filters.company) {
      filtered = filtered.filter(record => {
        const company = record['公司名稱'] || record['Company Name'] || record['Company'];
        return company === filters.company;
      });
    }

    onFilterChange(filtered);
  };

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleDateRangeChange = (value) => {
    setFilters(prev => ({ ...prev, dateRange: value }));
  };

  const handleTagToggle = (tag) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleCompanyChange = (value) => {
    setFilters(prev => ({ ...prev, company: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      dateRange: 'all',
      tags: [],
      company: ''
    });
  };

  const hasActiveFilters = filters.search || filters.dateRange !== 'all' || filters.tags.length > 0 || filters.company;

  return (
    <div className="record-filter">
      <div className="filter-header">
        <h3>篩選和搜尋</h3>
        <button 
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="toggle-advanced-btn"
        >
          {showAdvanced ? '收起' : '展開'}
        </button>
      </div>

      {/* 基本搜尋 */}
      <div className="basic-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="搜尋名片內容..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <select 
          value={filters.dateRange}
          onChange={(e) => handleDateRangeChange(e.target.value)}
          className="date-filter"
        >
          <option value="all">所有時間</option>
          <option value="today">今天</option>
          <option value="week">過去一週</option>
          <option value="month">過去一月</option>
          <option value="year">過去一年</option>
        </select>
      </div>

      {/* 進階篩選 */}
      {showAdvanced && (
        <div className="advanced-filters">
          {/* 標籤篩選 */}
          {availableTags.length > 0 && (
            <div className="filter-section">
              <label>標籤篩選:</label>
              <div className="tag-filters">
                {availableTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`tag-filter-btn ${filters.tags.includes(tag) ? 'active' : ''}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 公司篩選 */}
          {availableCompanies.length > 0 && (
            <div className="filter-section">
              <label>公司篩選:</label>
              <select 
                value={filters.company}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="company-filter"
              >
                <option value="">所有公司</option>
                {availableCompanies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* 清除篩選 */}
      {hasActiveFilters && (
        <div className="filter-actions">
          <button onClick={clearFilters} className="clear-filters-btn">
            清除所有篩選
          </button>
        </div>
      )}

      <style jsx="true">{`
        .record-filter {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 
            0 4px 16px rgba(31, 38, 135, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .filter-header h3 {
          margin: 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 18px;
          font-weight: 600;
        }

        .toggle-advanced-btn {
          padding: 6px 12px;
          background: rgba(255, 255, 255, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 8px;
          color: rgba(108, 99, 143, 0.9);
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .toggle-advanced-btn:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
        }

        .basic-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }

        .search-box {
          flex: 1;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 10px 40px 10px 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-size: 14px;
          color: rgba(108, 99, 143, 0.9);
          outline: none;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .search-input:focus {
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
        }

        .search-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(108, 99, 143, 0.6);
        }

        .date-filter, .company-filter {
          padding: 10px 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-size: 14px;
          color: rgba(108, 99, 143, 0.9);
          outline: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .date-filter:focus, .company-filter:focus {
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
        }

        .advanced-filters {
          border-top: 1px solid rgba(255, 255, 255, 0.3);
          padding-top: 16px;
        }

        .filter-section {
          margin-bottom: 16px;
        }

        .filter-section label {
          display: block;
          margin-bottom: 8px;
          color: rgba(108, 99, 143, 0.8);
          font-size: 14px;
          font-weight: 600;
        }

        .tag-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tag-filter-btn {
          padding: 6px 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.3);
          color: rgba(108, 99, 143, 0.9);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tag-filter-btn:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
        }

        .tag-filter-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .filter-actions {
          display: flex;
          justify-content: center;
          padding-top: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.3);
        }

        .clear-filters-btn {
          padding: 8px 16px;
          background: rgba(255, 107, 107, 0.3);
          border: 1px solid rgba(255, 107, 107, 0.4);
          border-radius: 8px;
          color: rgba(180, 60, 60, 0.9);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-filters-btn:hover {
          background: rgba(255, 107, 107, 0.4);
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .basic-filters {
            flex-direction: column;
          }
          
          .filter-header {
            flex-direction: column;
            gap: 8px;
            text-align: center;
          }
          
          .toggle-advanced-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default RecordFilter;